/**
 * Request Deduplication Service
 * 
 * Advanced request deduplication system that works with React Query to eliminate
 * duplicate API calls and improve performance through intelligent caching and
 * request consolidation.
 * 
 * Features:
 * - Query key normalization for better cache hits
 * - Request batching for similar queries
 * - Intelligent cache invalidation
 * - Performance monitoring and metrics
 */

import { QueryKey } from '@tanstack/react-query';

// Request metadata for tracking and optimization
export interface RequestMetadata {
  queryKey: QueryKey;
  timestamp: number;
  hash: string;
  useCase: string;
  priority: 'high' | 'medium' | 'low';
}

// Deduplication statistics
export interface DeduplicationStats {
  totalRequests: number;
  deduplicatedRequests: number;
  cacheHits: number;
  cacheMisses: number;
  averageResponseTime: number;
  duplicatesSaved: number;
}

// Query key normalization rules
interface NormalizationRule {
  pattern: RegExp;
  normalizer: (key: QueryKey) => QueryKey;
  description: string;
}

/**
 * Request Deduplication Service Class
 */
export class RequestDeduplicationService {
  private requestCache: Map<string, RequestMetadata> = new Map();
  private stats: DeduplicationStats = {
    totalRequests: 0,
    deduplicatedRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    averageResponseTime: 0,
    duplicatesSaved: 0
  };
  private responseTimeHistory: number[] = [];
  private readonly maxHistorySize = 100;
  private readonly deduplicationWindow = 5000; // 5 seconds

  // Query key normalization rules
  private normalizationRules: NormalizationRule[] = [
    {
      pattern: /shopify-products-infinite/,
      normalizer: this.normalizeInfiniteQuery.bind(this),
      description: 'Normalize infinite scroll queries'
    },
    {
      pattern: /shopify-products/,
      normalizer: this.normalizeProductQuery.bind(this),
      description: 'Normalize Shopify product queries'
    },
    {
      pattern: /shopify-collections/,
      normalizer: this.normalizeCollectionQuery.bind(this),
      description: 'Normalize Shopify collection queries'
    }
  ];

  /**
   * Normalize a query key to improve cache hit rates
   */
  normalizeQueryKey(queryKey: QueryKey): QueryKey {
    const keyString = JSON.stringify(queryKey);
    
    for (const rule of this.normalizationRules) {
      if (rule.pattern.test(keyString)) {
        return rule.normalizer(queryKey);
      }
    }
    
    return queryKey;
  }

  /**
   * Normalize product query keys
   */
  private normalizeProductQuery(queryKey: QueryKey): QueryKey {
    if (!Array.isArray(queryKey)) return queryKey;
    
    const [baseKey, ...params] = queryKey;
    
    // Normalize parameters to consistent format
    const normalizedParams = params.map(param => {
      if (typeof param === 'object' && param !== null) {
        // Sort object keys for consistent hashing
        const sortedObj: Record<string, any> = {};
        Object.keys(param).sort().forEach(key => {
          sortedObj[key] = param[key];
        });
        return sortedObj;
      }
      return param;
    });
    
    return [baseKey, ...normalizedParams];
  }

  /**
   * Normalize collection query keys
   */
  private normalizeCollectionQuery(queryKey: QueryKey): QueryKey {
    if (!Array.isArray(queryKey)) return queryKey;
    
    const [baseKey, first, after, sortKey, reverse, optimized] = queryKey;
    
    // Provide defaults for undefined values
    return [
      baseKey,
      first || 20,
      after || null,
      sortKey || 'TITLE',
      reverse || false,
      optimized || 'optimized'
    ];
  }

  /**
   * Normalize infinite query keys
   */
  private normalizeInfiniteQuery(queryKey: QueryKey): QueryKey {
    if (!Array.isArray(queryKey)) return queryKey;
    
    const [baseKey, first, sortKey, reverse, query, optimized] = queryKey;
    
    return [
      baseKey,
      first || 20,
      sortKey || 'TITLE',
      reverse || false,
      query || null,
      optimized || 'optimized'
    ];
  }

  /**
   * Generate a hash for a query key
   */
  generateQueryHash(queryKey: QueryKey): string {
    const normalizedKey = this.normalizeQueryKey(queryKey);
    const keyString = JSON.stringify(normalizedKey);
    
    // Simple hash function for query keys
    let hash = 0;
    for (let i = 0; i < keyString.length; i++) {
      const char = keyString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Ensure positive hash and convert to base36
    return Math.abs(hash).toString(36);
  }

  /**
   * Check if a request should be deduplicated
   */
  shouldDeduplicate(queryKey: QueryKey, useCase: string = 'default'): boolean {
    const hash = this.generateQueryHash(queryKey);
    const existingRequest = this.requestCache.get(hash);
    
    this.stats.totalRequests++;
    
    if (existingRequest) {
      const timeDiff = Date.now() - existingRequest.timestamp;
      
      if (timeDiff < this.deduplicationWindow) {
        this.stats.deduplicatedRequests++;
        this.stats.duplicatesSaved++;
        return true; // Should deduplicate
      } else {
        // Request is old, remove from cache
        this.requestCache.delete(hash);
      }
    }
    
    // Record new request
    this.requestCache.set(hash, {
      queryKey: this.normalizeQueryKey(queryKey),
      timestamp: Date.now(),
      hash,
      useCase,
      priority: this.determinePriority(useCase)
    });
    
    return false; // Should not deduplicate
  }

  /**
   * Determine request priority based on use case
   */
  private determinePriority(useCase: string): 'high' | 'medium' | 'low' {
    const highPriorityUseCases = ['cart', 'checkout', 'product-detail'];
    const lowPriorityUseCases = ['search', 'filter', 'background-refresh'];
    
    if (highPriorityUseCases.includes(useCase)) {
      return 'high';
    } else if (lowPriorityUseCases.includes(useCase)) {
      return 'low';
    }
    
    return 'medium';
  }

  /**
   * Record cache hit
   */
  recordCacheHit(responseTime?: number) {
    this.stats.cacheHits++;
    if (responseTime !== undefined) {
      this.recordResponseTime(responseTime);
    }
  }

  /**
   * Record cache miss
   */
  recordCacheMiss(responseTime?: number) {
    this.stats.cacheMisses++;
    if (responseTime !== undefined) {
      this.recordResponseTime(responseTime);
    }
  }

  /**
   * Record response time for performance tracking
   */
  private recordResponseTime(responseTime: number) {
    this.responseTimeHistory.push(responseTime);
    
    // Keep history size manageable
    if (this.responseTimeHistory.length > this.maxHistorySize) {
      this.responseTimeHistory.shift();
    }
    
    // Update average response time
    this.stats.averageResponseTime = 
      this.responseTimeHistory.reduce((sum, time) => sum + time, 0) / 
      this.responseTimeHistory.length;
  }

  /**
   * Get deduplication statistics
   */
  getStats(): DeduplicationStats {
    return { ...this.stats };
  }

  /**
   * Get cache efficiency metrics
   */
  getCacheEfficiency(): {
    hitRate: number;
    deduplicationRate: number;
    averageResponseTime: number;
    totalSavings: number;
  } {
    const totalCacheRequests = this.stats.cacheHits + this.stats.cacheMisses;
    const hitRate = totalCacheRequests > 0 ? this.stats.cacheHits / totalCacheRequests : 0;
    const deduplicationRate = this.stats.totalRequests > 0 ? 
      this.stats.deduplicatedRequests / this.stats.totalRequests : 0;
    
    return {
      hitRate: Math.round(hitRate * 100) / 100,
      deduplicationRate: Math.round(deduplicationRate * 100) / 100,
      averageResponseTime: Math.round(this.stats.averageResponseTime),
      totalSavings: this.stats.duplicatesSaved
    };
  }

  /**
   * Clear old cache entries
   */
  cleanupCache() {
    const now = Date.now();
    const expiredEntries: string[] = [];
    
    this.requestCache.forEach((metadata, hash) => {
      if (now - metadata.timestamp > this.deduplicationWindow * 2) {
        expiredEntries.push(hash);
      }
    });
    
    expiredEntries.forEach(hash => {
      this.requestCache.delete(hash);
    });
    
    return expiredEntries.length;
  }

  /**
   * Reset all statistics and cache
   */
  reset() {
    this.requestCache.clear();
    this.responseTimeHistory = [];
    this.stats = {
      totalRequests: 0,
      deduplicatedRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      averageResponseTime: 0,
      duplicatesSaved: 0
    };
  }

  /**
   * Get active cache entries
   */
  getActiveCacheEntries(): RequestMetadata[] {
    return Array.from(this.requestCache.values());
  }

  /**
   * Batch similar queries for optimization
   */
  getBatchableQueries(queryKeys: QueryKey[]): QueryKey[][] {
    const batches: Map<string, QueryKey[]> = new Map();
    
    queryKeys.forEach(queryKey => {
      const batchKey = this.getBatchKey(queryKey);
      if (!batches.has(batchKey)) {
        batches.set(batchKey, []);
      }
      batches.get(batchKey)!.push(queryKey);
    });
    
    return Array.from(batches.values()).filter(batch => batch.length > 1);
  }

  /**
   * Generate batch key for similar queries
   */
  private getBatchKey(queryKey: QueryKey): string {
    if (!Array.isArray(queryKey)) return 'single';
    
    const [baseKey] = queryKey;
    return typeof baseKey === 'string' ? baseKey : 'unknown';
  }

  /**
   * Add custom normalization rule
   */
  addNormalizationRule(rule: NormalizationRule) {
    this.normalizationRules.push(rule);
  }

  /**
   * Remove normalization rule
   */
  removeNormalizationRule(pattern: RegExp) {
    this.normalizationRules = this.normalizationRules.filter(
      rule => rule.pattern.source !== pattern.source
    );
  }
}

// Default export - singleton instance
const requestDeduplicationService = new RequestDeduplicationService();
export default requestDeduplicationService;

// Named exports
export { requestDeduplicationService };
